import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid, MenuItem,
  Paper, Radio, RadioGroup, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PrintIcon from '@mui/icons-material/Print';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate, useParams } from 'react-router-dom';
import {
  cancelRental, extendRental, getRentalAgreementTemplate, getRentalById,
  payRentalInvoice, returnRental, uploadRentalAgreement
} from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { printRentalInvoice } from '../../utils/rentalInvoicePrint';
import { printRentalAgreement } from '../../utils/rentalAgreementPrint';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import {
  billingSummary, extraUntilDate, cycleTitle, durationLabel,
  daysInclusive, suggestedReturnRent, invoicesKeptOnReturn
} from '../../utils/rentalBilling';
import {
  money, RentalPageHeader, RentalSection, RentalSourceChip, RentalStatCard,
  RentalStatusChip, tableSx
} from './rentalUi';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const RentalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payInv, setPayInv] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');
  const [payNotes, setPayNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [returnNotes, setReturnNotes] = useState('');
  const [returnChecks, setReturnChecks] = useState([]);
  const [billableDays, setBillableDays] = useState(1);
  const [rentAmount, setRentAmount] = useState('');
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendUntil, setExtendUntil] = useState('');
  const [extendCount, setExtendCount] = useState(1);
  const [extendCycle, setExtendCycle] = useState('monthly');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalById(id);
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load rental'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try {
      setBusy(true);
      setError('');
      await fn();
      await load();
      if (okMsg) setError('');
    } catch (err) {
      setError(getErrorMessage(err, 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  const openExtend = () => {
    const currentEnd = data?.end_date || data?.return_date;
    const cycle = data?.billing_cycle || 'monthly';
    setExtendCycle(cycle);
    setExtendCount(1);
    setExtendUntil(currentEnd ? extraUntilDate(currentEnd, cycle, 1) : '');
    setExtendOpen(true);
  };

  const applyExtendExtra = (count, cycle) => {
    const currentEnd = data?.end_date || data?.return_date;
    const nextCount = Math.max(1, parseInt(count, 10) || 1);
    setExtendCount(nextCount);
    setExtendCycle(cycle);
    if (currentEnd) setExtendUntil(extraUntilDate(currentEnd, cycle, nextCount));
  };

  const saveExtend = async () => {
    if (!extendUntil) {
      setError('Select the new end date');
      return;
    }
    try {
      setBusy(true);
      setError('');
      await extendRental(id, {
        until_date: extendUntil,
        extra_periods: extendCount,
        extra_cycle: extendCycle
      });
      setExtendOpen(false);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Extend failed'));
    } finally {
      setBusy(false);
    }
  };

  const initReturnForm = (rental, date) => {
    const start = rental?.start_date || rental?.rental_date;
    const days = daysInclusive(start, date);
    setReturnDate(date);
    setBillableDays(days);
    setReturnNotes('');
    setReturnChecks((rental?.items || []).map((it) => ({
      id: it.id,
      name: it.product_name,
      serial: (it.serials || []).join(', ') || it.serial_number || '',
      condition: 'good',
      damage_amount: '',
      damage_notes: ''
    })));
    const kept = invoicesKeptOnReturn(rental?.invoices || [], date);
    setRentAmount(kept.length === 0 ? String(suggestedReturnRent(rental, days)) : '0');
  };

  const applyReturnDate = (date) => {
    const start = data?.start_date || data?.rental_date;
    const days = daysInclusive(start, date);
    setReturnDate(date);
    setBillableDays(days);
    const kept = invoicesKeptOnReturn(data?.invoices || [], date);
    if (kept.length === 0) setRentAmount(String(suggestedReturnRent(data, days)));
  };

  const applyBillableDays = (days) => {
    const next = Math.max(1, parseInt(days, 10) || 1);
    setBillableDays(next);
    const kept = invoicesKeptOnReturn(data?.invoices || [], returnDate);
    if (kept.length === 0) setRentAmount(String(suggestedReturnRent(data, next)));
  };

  const updateReturnCheck = (itemId, patch) => {
    setReturnChecks((rows) => rows.map((row) => (
      row.id === itemId ? { ...row, ...patch } : row
    )));
  };

  const saveReturn = async () => {
    const damagedMissingPrice = returnChecks.find((row) => (
      row.condition === 'damaged' && !(parseFloat(row.damage_amount) > 0)
    ));
    if (damagedMissingPrice) {
      setError(`Enter damage price for ${damagedMissingPrice.name}`);
      return;
    }
    const kept = invoicesKeptOnReturn(data?.invoices || [], returnDate);
    const rent = parseFloat(rentAmount) || 0;
    if (kept.length === 0 && !(rent > 0)) {
      setError('Enter days and rent price to generate the invoice');
      return;
    }
    try {
      setBusy(true);
      setError('');
      await returnRental(id, {
        return_date: returnDate,
        return_notes: returnNotes.trim(),
        billable_days: billableDays,
        rent_amount: rent,
        items: returnChecks.map((row) => ({
          id: row.id,
          condition: row.condition,
          damage_amount: row.condition === 'damaged' ? parseFloat(row.damage_amount) || 0 : 0,
          damage_notes: row.damage_notes
        }))
      });
      setReturnOpen(false);
      setReturnNotes('');
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Return failed'));
    } finally {
      setBusy(false);
    }
  };

  const savePay = async () => {
    if (!payInv) return;
    try {
      setBusy(true);
      await payRentalInvoice(payInv.id, {
        amount: parseFloat(payAmount),
        payment_method: payMethod,
        payment_notes: payNotes
      });
      setPayInv(null);
      setPayAmount('');
      setPayNotes('');
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Payment failed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (error && !data) return <Alert severity="error">{error}</Alert>;

  const r = data;
  const open = ['Active', 'Overdue'].includes(r.status);

  return (
    <Box>
      <RentalPageHeader
        backTo="/rentals"
        backLabel="All rentals"
        title={r.rental_number}
        subtitle={(
          <>
            {r.customer?.name}
            {r.customer?.phone ? ` · ${r.customer.phone}` : ''}
            {r.customer?.id ? (
              <Button size="small" onClick={() => navigate(`/rentals/customers/${r.customer.id}`)}>
                Profile
              </Button>
            ) : null}
          </>
        )}
        actions={(
          <>
            <RentalSourceChip source={r.rental_source} label={r.source_label} />
            <RentalStatusChip status={r.status} />
          </>
        )}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        {[
          { label: 'Billing', value: billingSummary(r.billing_cycle, r.duration_count), accent: 'primary' },
          { label: 'Period', value: `${r.start_date || r.rental_date} → ${r.end_date || r.return_date}`, accent: 'info' },
          ...(r.actual_return_date ? [{ label: 'Returned', value: r.actual_return_date, accent: 'success' }] : []),
          { label: 'Billed', value: money(r.billed), accent: 'primary' },
          { label: 'Paid', value: money(r.paid), accent: 'success' },
          { label: 'Balance', value: money(r.balance), accent: Number(r.balance) > 0 ? 'error' : 'success' },
          { label: 'Deposit', value: money(r.deposit), accent: 'warning' },
          ...(r.rental_source === 'outside' ? [
            {
              label: 'Outside rent cost',
              value: money((r.items || []).reduce((s, it) => s + Number(it.outside_rent || 0), 0)),
              accent: 'warning'
            },
            {
              label: 'Customer rent',
              value: money((r.items || []).reduce((s, it) => s + Number(it.period_rate || 0) * (it.quantity || 1), 0)),
              accent: 'info'
            }
          ] : [])
        ].map((card) => (
          <Grid item xs={6} sm={4} md={2} key={card.label}>
            <RentalStatCard label={card.label} value={card.value} accent={card.accent} />
          </Grid>
        ))}
      </Grid>

      <Paper sx={{
        p: 1.5, mb: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
        display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'
      }}>
        {open && (
          <>
            <Button
              variant="contained"
              startIcon={<AssignmentReturnIcon />}
              disabled={busy}
              onClick={() => {
                initReturnForm(r, new Date().toISOString().slice(0, 10));
                setReturnOpen(true);
              }}
            >
              Return items
            </Button>
            <Button startIcon={<EventRepeatIcon />} disabled={busy} onClick={openExtend}>
              Extend
            </Button>
            <Button
              color="error"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Cancel this rental and unpaid invoices?')) {
                  act(() => cancelRental(r.id));
                }
              }}
            >
              Cancel rental
            </Button>
            <Divider flexItem orientation="vertical" />
          </>
        )}
        <Button
          startIcon={<PrintIcon />}
          disabled={busy}
          onClick={async () => {
            try {
              const tpl = await getRentalAgreementTemplate();
              printRentalAgreement(r, tpl?.data || null);
            } catch (err) {
              setError(getErrorMessage(err, 'Could not load agreement template'));
            }
          }}
        >
          Print agreement
        </Button>
        <Button startIcon={<UploadFileIcon />} component="label" disabled={busy}>
          {r.agreement_file ? 'Replace signed agreement' : 'Upload signed agreement'}
          <input
            hidden
            type="file"
            accept=".pdf,image/*,.doc,.docx"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              try {
                setBusy(true);
                const data = await fileToBase64(file);
                await uploadRentalAgreement(r.id, {
                  filename: file.name,
                  mime: file.type || 'application/pdf',
                  data
                });
                await load();
              } catch (err) {
                setError(getErrorMessage(err, 'Failed to upload agreement'));
              } finally {
                setBusy(false);
              }
            }}
          />
        </Button>
        {r.agreement_url ? (
          <Button href={resolvePhotoUrl(r.agreement_url)} target="_blank" rel="noreferrer">
            Open signed file
          </Button>
        ) : null}
      </Paper>

      {r.return_notes ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>Return note</Typography>
          {r.actual_return_date ? `Returned ${r.actual_return_date}. ` : ''}
          {r.return_notes}
        </Alert>
      ) : null}

      <RentalSection title="Items">
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Serial</TableCell>
              {r.rental_source === 'outside' ? <TableCell>Purchase</TableCell> : null}
              {r.rental_source === 'outside' ? <TableCell>Asset value</TableCell> : null}
              {r.rental_source === 'outside' ? <TableCell>Outside rent</TableCell> : null}
              {r.rental_source === 'outside' ? <TableCell>Components</TableCell> : null}
              <TableCell align="right">{r.rental_source === 'outside' ? 'Customer price' : 'Period rate'}</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(r.items || []).map((it) => (
              <TableRow key={it.id}>
                <TableCell>{it.product_name}</TableCell>
                <TableCell>
                  {(it.serials || []).join(', ') || it.serial_number || '—'}
                  {it.serial_number ? (
                    <Button size="small" onClick={() => navigate(`/rentals/track?serial=${encodeURIComponent(it.serial_number)}`)}>
                      Track
                    </Button>
                  ) : null}
                </TableCell>
                {r.rental_source === 'outside' ? (
                  <TableCell>
                    {it.purchase_date || '—'}
                    {it.purchase_price != null ? (
                      <Typography variant="caption" display="block">{money(it.purchase_price)}</Typography>
                    ) : null}
                  </TableCell>
                ) : null}
                {r.rental_source === 'outside' ? <TableCell>{money(it.asset_value)}</TableCell> : null}
                {r.rental_source === 'outside' ? (
                  <TableCell>
                    {money(it.outside_rent)}
                    {it.outside_duration ? (
                      <Typography variant="caption" display="block">
                        {it.outside_duration} {cycleTitle(it.outside_cycle).toLowerCase()}
                      </Typography>
                    ) : null}
                  </TableCell>
                ) : null}
                {r.rental_source === 'outside' ? (
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{it.components || '—'}</TableCell>
                ) : null}
                <TableCell align="right">{money(it.period_rate)}</TableCell>
                <TableCell>
                  <RentalStatusChip status={it.item_status} />
                  {it.damage_notes ? (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {it.damage_notes}
                      {it.damage_amount > 0 ? ` · ${money(it.damage_amount)}` : ''}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </RentalSection>

      <RentalSection
        title="Invoices"
        action={(
          <Button size="small" onClick={() => navigate(`/rentals/invoices?rental_id=${r.id}`)}>
            Invoice monitoring
          </Button>
        )}
      >
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Invoice</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Due</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(r.invoices || []).map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>{inv.invoice_number}</TableCell>
                <TableCell>{inv.period_start} → {inv.period_end}</TableCell>
                <TableCell>{inv.due_date}</TableCell>
                <TableCell align="right">{money(inv.amount)}</TableCell>
                <TableCell align="right">{money(inv.paid_amount)}</TableCell>
                <TableCell>
                  <RentalStatusChip status={inv.display_status} />
                </TableCell>
                <TableCell>
                  <Button size="small" onClick={() => navigate(`/rentals/invoices/${inv.id}`)}>
                    View invoice
                  </Button>
                  <Button
                    size="small"
                    onClick={() => printRentalInvoice({
                      ...inv,
                      customer: r.customer,
                      rental_number: r.rental_number,
                      billing_cycle: r.billing_cycle,
                      items: r.items,
                      start_date: r.start_date || r.rental_date,
                      end_date: r.end_date || r.return_date,
                      deposit: r.deposit
                    })}
                  >
                    Print
                  </Button>
                  {['pending', 'partial', 'overdue'].includes(inv.display_status) && (
                    <Button
                      size="small"
                      onClick={() => {
                        setPayInv(inv);
                        setPayAmount(String(inv.balance || inv.amount));
                      }}
                    >
                      Pay
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(r.invoices || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No invoices yet. They are created on due dates, or when you return items.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </RentalSection>

      <Dialog open={extendOpen} onClose={() => setExtendOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Extend rental</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Current end: {r.end_date || r.return_date || '—'}. Choose extra days, weeks, or months, or pick a new end date.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Add extra"
                value={extendCycle}
                onChange={(e) => applyExtendExtra(extendCount, e.target.value)}
              >
                <MenuItem value="daily">Days</MenuItem>
                <MenuItem value="weekly">Weeks</MenuItem>
                <MenuItem value="monthly">Months</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                fullWidth
                label={durationLabel(extendCycle)}
                value={extendCount}
                onChange={(e) => applyExtendExtra(e.target.value, extendCycle)}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                type="date"
                fullWidth
                required
                label="New end date"
                value={extendUntil}
                onChange={(e) => setExtendUntil(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveExtend} disabled={busy || !extendUntil}>
            Extend
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>Return items</DialogTitle>
        <DialogContent>
          {(() => {
            const kept = invoicesKeptOnReturn(r.invoices || [], returnDate);
            const needsInvoice = kept.length === 0;
            const damageTotal = returnChecks.reduce((sum, row) => (
              row.condition === 'damaged' ? sum + (parseFloat(row.damage_amount) || 0) : sum
            ), 0);
            const rent = parseFloat(rentAmount) || 0;
            const allGood = returnChecks.length > 0 && returnChecks.every((row) => row.condition === 'good');
            return (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Check each item, then enter days and price. Damaged items get a separate damage invoice.
                  {r.end_date || r.return_date ? ` Contract end ${r.end_date || r.return_date}.` : ''}
                </Typography>
                {needsInvoice ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No invoice yet — enter days and rent price to generate it on return.
                  </Alert>
                ) : null}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="date"
                      fullWidth
                      label="Return date"
                      value={returnDate}
                      onChange={(e) => applyReturnDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={allGood}
                          onChange={(e) => {
                            const good = e.target.checked;
                            setReturnChecks((rows) => rows.map((row) => ({
                              ...row,
                              condition: good ? 'good' : row.condition,
                              damage_amount: good ? '' : row.damage_amount,
                              damage_notes: good ? '' : row.damage_notes
                            })));
                          }}
                        />
                      )}
                      label="All items are good"
                    />
                  </Grid>
                </Grid>
                <Stack spacing={1.25} sx={{ mb: 2 }}>
                  {returnChecks.map((row) => (
                    <Paper
                      key={row.id}
                      variant="outlined"
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        borderColor: row.condition === 'damaged' ? 'error.light' : 'success.light',
                        bgcolor: (t) => (t.palette.mode === 'dark'
                          ? 'transparent'
                          : (row.condition === 'damaged' ? '#fff8f7' : '#f4faf6'))
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography fontWeight={700}>{row.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.serial || 'No serial'}
                          </Typography>
                        </Box>
                        <FormControl>
                          <RadioGroup
                            row
                            value={row.condition}
                            onChange={(e) => updateReturnCheck(row.id, {
                              condition: e.target.value,
                              damage_amount: e.target.value === 'good' ? '' : row.damage_amount,
                              damage_notes: e.target.value === 'good' ? '' : row.damage_notes
                            })}
                          >
                            <FormControlLabel value="good" control={<Radio color="success" />} label="Good" />
                            <FormControlLabel value="damaged" control={<Radio color="error" />} label="Damaged" />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                      {row.condition === 'damaged' ? (
                        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              required
                              type="number"
                              label="Damage price"
                              value={row.damage_amount}
                              onChange={(e) => updateReturnCheck(row.id, { damage_amount: e.target.value })}
                              inputProps={{ min: 0, step: '0.01' }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              fullWidth
                              label="Damage note"
                              value={row.damage_notes}
                              onChange={(e) => updateReturnCheck(row.id, { damage_notes: e.target.value })}
                              placeholder="What is damaged or missing"
                            />
                          </Grid>
                        </Grid>
                      ) : null}
                    </Paper>
                  ))}
                </Stack>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Invoice</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required={needsInvoice}
                      type="number"
                      label="Days used"
                      value={billableDays}
                      onChange={(e) => applyBillableDays(e.target.value)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required={needsInvoice}
                      type="number"
                      label="Rent price"
                      value={rentAmount}
                      onChange={(e) => setRentAmount(e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }}
                      helperText={needsInvoice ? 'Invoice amount for the days used' : 'Leave 0 if already invoiced'}
                    />
                  </Grid>
                </Grid>
                <Box sx={{
                  mt: 2, p: 1.75, borderRadius: 2,
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(66,165,245,0.12)' : '#e3f2fd')
                }}>
                  <Typography variant="body2">
                    Rent {money(rent)}{damageTotal > 0 ? ` + damage ${money(damageTotal)}` : ''}
                    {' = '}
                    <strong>{money(rent + damageTotal)}</strong>
                    {needsInvoice || damageTotal > 0 ? ' — invoice(s) will be generated on return.' : ''}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Extra return note (optional)"
                  placeholder="Accessories returned, customer comments…"
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveReturn}
            disabled={busy || returnChecks.length === 0}
          >
            {invoicesKeptOnReturn(r.invoices || [], returnDate).length === 0
              || returnChecks.some((row) => row.condition === 'damaged')
              || (parseFloat(rentAmount) || 0) > 0
              ? 'Generate invoice & return'
              : 'Confirm return'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(payInv)} onClose={() => setPayInv(null)} fullWidth maxWidth="xs">
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {payInv?.invoice_number} · Balance {money(payInv?.balance)}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            fullWidth
            label="Method"
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value)}
            sx={{ mb: 2 }}
          >
            {['Cash', 'Bank Transfer', 'Card', 'Cheque', 'Other'].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Notes"
            value={payNotes}
            onChange={(e) => setPayNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayInv(null)}>Cancel</Button>
          <Button variant="contained" onClick={savePay} disabled={busy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentalView;
